import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { staffAPI, GENDER_OPTIONS } from '../../api/staff';
import { formatPhoneInput } from '../../lib/validators';
import { getStaffPhotoUrl } from '../../lib/fileUrls';
import UserAvatar from '../../components/UserAvatar';
import { ArrowLeft, Save, Camera, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

function Section({ title, icon, collapsible = false, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="soem-card overflow-hidden">
      <button type="button" onClick={collapsible ? () => setOpen(!open) : undefined}
        className={`w-full flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50 ${collapsible ? 'cursor-pointer hover:bg-slate-100' : ''}`}>
        <div className="flex items-center gap-2">
          {icon && <span className="text-lg">{icon}</span>}
          <h3 className="font-bold text-soem-navy text-sm">{title}</h3>
        </div>
        {collapsible && (open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />)}
      </button>
      {open && <div className="p-5 space-y-4">{children}</div>}
    </div>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-sm text-slate-700">{label}</span>
      <button type="button" onClick={() => onChange(!value)}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-all ${value ? 'bg-orange-500 border-orange-500 text-white' : 'bg-slate-100 border-slate-300 text-slate-500'}`}>
        <span className={`w-4 h-4 rounded-full bg-white shadow transition-transform`} />
        {value ? 'SÍ' : 'NO'}
      </button>
    </div>
  );
}

// Profile completion calculation
function calcCompletion(form) {
  const checks = [
    !!form.first_name, !!form.last_name, !!form.phone,
    !!form.gender, !!form.age,
    !!form.address?.city,
    !!form.bank_info?.bank,
    !!form.emergency_contact?.name && !!form.emergency_contact?.phone,
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export default function StaffForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const photoRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', gender: '', age: '', position: '', status: 'activo',
    phone: '', email: '',
    address: { street: '', ext_number: '', int_number: '', colony: '', postal_code: '', city: '', state: '' },
    bank_info: { bank: '', account_number: '', clabe: '', account_holder: '' },
    medical_info: { has_condition: false, condition_detail: '', uses_substances: false, has_disability: false, disability_detail: '' },
    imss_number: '',
    emergency_contact: { name: '', relationship: '', phone: '', email: '' },
  });

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));
  const setNested = (section, key, value) => setForm(f => ({ ...f, [section]: { ...f[section], [key]: value } }));

  useEffect(() => {
    if (isEdit) {
      // Load from external API and local extended data in parallel
      Promise.all([
        staffAPI.get(id),
        staffAPI.getExtended(id).catch(() => ({ data: {} })),
      ]).then(([r, extR]) => {
        const s = r.data;
        const ext = extR.data || {};
        
        // Map address fields - API returns 'neighborhood' but we use 'colony' in form
        const apiAddress = s.address || {};
        const formAddress = {
          street: apiAddress.street || '',
          ext_number: apiAddress.ext_number || '',
          int_number: apiAddress.int_number || '',
          colony: apiAddress.neighborhood || apiAddress.colony || '', // API uses 'neighborhood'
          postal_code: apiAddress.postal_code || '',
          city: apiAddress.city || '',
          state: apiAddress.state || '',
        };
        
        // IMSS can come from external API (as social_security_number) or local extended data
        const imssNumber = s.social_security_number || ext.imss_number || '';
        
        setForm({
          first_name: s.first_name || '', last_name: s.last_name || '',
          gender: s.gender || '', age: s.age || '', position: s.position || '', status: s.status || 'activo',
          phone: s.phone || '', email: s.email || '',
          address: formAddress,
          // Bank and medical come from local extended data
          bank_info: ext.bank_info || { bank: '', account_number: '', clabe: '', account_holder: '' },
          medical_info: ext.medical_info || { has_condition: false, condition_detail: '', uses_substances: false, has_disability: false, disability_detail: '' },
          imss_number: imssNumber,
          emergency_contact: s.emergency_contact || { name: '', relationship: '', phone: '', email: '' },
        });
        
        // Handle photo URL - use centralized helper
        if (s.photo) {
          setPhotoPreview(getStaffPhotoUrl(s.photo));
        }
      }).catch(() => toast.error('Error al cargar'));
    }
  }, [id, isEdit]);

  const completion = calcCompletion(form);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) { toast.error('Nombre y apellidos son requeridos'); return; }
    setSaving(true);
    try {
      // Build address with neighborhood (API field name)
      const addressPayload = {
        street: form.address.street || null,
        ext_number: form.address.ext_number || null,
        int_number: form.address.int_number || null,
        neighborhood: form.address.colony || null, // colony -> neighborhood for API
        postal_code: form.address.postal_code || null,
        city: form.address.city || null,
        state: form.address.state || null,
      };
      const hasAddress = Object.values(addressPayload).some(Boolean);

      const payload = {
        first_name: form.first_name, last_name: form.last_name, gender: form.gender || null,
        age: form.age ? parseInt(form.age) : null, position: form.position || null, status: form.status,
        phone: form.phone || null, email: form.email || null,
        address: hasAddress ? addressPayload : null,
        emergency_contact: form.emergency_contact.name ? form.emergency_contact : null,
        social_security_number: form.imss_number || null, // API uses social_security_number
      };
      
      let staffId = id;
      if (isEdit) {
        await staffAPI.update(id, payload);
        toast.success('Datos actualizados');
      } else {
        const r = await staffAPI.create(payload);
        staffId = r.data.id;
        toast.success('Personal creado');
      }
      
      // Save bank + medical to local backend (external API doesn't accept these fields)
      // Always save extended data if any field has value
      const extendedData = {
        bank_info: {
          bank: form.bank_info?.bank || '',
          account_holder: form.bank_info?.account_holder || '',
          account_number: form.bank_info?.account_number || '',
          clabe: form.bank_info?.clabe || '',
        },
        medical_info: {
          has_condition: form.medical_info?.has_condition || false,
          condition_detail: form.medical_info?.condition_detail || '',
          uses_substances: form.medical_info?.uses_substances || false,
          has_disability: form.medical_info?.has_disability || false,
          disability_detail: form.medical_info?.disability_detail || '',
        },
        imss_number: form.imss_number || '',
      };
      
      if (staffId) {
        await staffAPI.setExtended(staffId, extendedData).catch(() => {});
      }
      
      // Upload photo if selected
      if (photoFile && staffId) {
        setUploadingPhoto(true);
        try {
          await staffAPI.uploadPhoto(staffId, photoFile);
          toast.success('Foto guardada correctamente');
        } catch {
          toast.error('La foto no pudo subirse. Puedes intentarlo desde Editar.');
        } finally {
          setUploadingPhoto(false);
        }
      }
      navigate('/dashboard/staff');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const missingFields = [];
  if (!form.bank_info?.bank) missingFields.push('datos bancarios');
  if (!form.emergency_contact?.name) missingFields.push('contacto de emergencia');

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/dashboard/staff')} className="soem-btn-secondary p-2" aria-label="Volver al directorio"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-soem-navy">{isEdit ? 'Editar Personal' : 'Nuevo Personal'}</h1>
          {/* Completion bar */}
          <div className="flex items-center gap-3 mt-1.5">
            <div className="flex-1 bg-slate-100 rounded-full h-1.5">
              <div className="h-1.5 rounded-full transition-all duration-500" style={{ width: `${completion}%`, backgroundColor: completion === 100 ? '#15803D' : completion >= 60 ? '#D97706' : '#DC2626' }} />
            </div>
            <span className="text-xs font-semibold text-slate-500 whitespace-nowrap">Perfil al {completion}%</span>
          </div>
          {missingFields.length > 0 && completion < 100 && (
            <p className="text-xs text-slate-400 mt-0.5">Completa {missingFields.join(' y ')} para el 100%</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* S1: Photo + Basic */}
        <Section title="Foto y datos básicos">
          <div className="flex items-start gap-6">
            <div className="relative group cursor-pointer" onClick={() => photoRef.current?.click()}>
              {photoPreview ? (
                <img src={photoPreview} className="w-20 h-20 rounded-full object-cover border-2 border-white shadow-lg" alt="preview" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                  <Camera size={24} className="text-slate-300" />
                </div>
              )}
              <div className="absolute inset-0 rounded-full bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Camera size={18} className="text-white" />
              </div>
              <input ref={photoRef} type="file" accept=".jpg,.jpeg,.png,.webp" capture="environment" className="hidden"
                onChange={e => {
                  const f = e.target.files[0];
                  if (!f) return;
                  if (f.size > 5 * 1024 * 1024) { toast.error('La foto no debe superar 5MB'); return; }
                  setPhotoFile(f);
                  setPhotoPreview(URL.createObjectURL(f));
                  toast.info('Foto seleccionada. Se guardará al presionar el botón.');
                }} />
            </div>
            <div className="flex-1 grid grid-cols-2 gap-3">
              <div className="col-span-2 grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="f-nombres" className="soem-label block mb-1">Nombre(s) *</label>
                  <input id="f-nombres" className={inputCls} value={form.first_name} onChange={e => set('first_name', e.target.value)} required />
                </div>
                <div>
                  <label htmlFor="f-apellidos" className="soem-label block mb-1">Apellidos *</label>
                  <input id="f-apellidos" className={inputCls} value={form.last_name} onChange={e => set('last_name', e.target.value)} required />
                </div>
              </div>
              <div>
                <label htmlFor="f-género" className="soem-label block mb-1">Género</label>
                <select id="f-género" className={inputCls} value={form.gender} onChange={e => set('gender', e.target.value)}>
                  <option value="">— Seleccionar —</option>
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                </select>
              </div>
              <div>
                <label htmlFor="f-edad" className="soem-label block mb-1">Edad</label>
                <input
id="f-edad"                   type="number"
                  min="16" max="80"
                  step="1"
                  className={inputCls}
                  value={form.age}
                  onChange={e => set('age', Math.floor(Number(e.target.value)) || '')}
                  onKeyDown={e => ['.', ',', 'e', 'E', '+', '-'].includes(e.key) && e.preventDefault()}
                  placeholder="25"
                />
              </div>
              <div>
                <label htmlFor="f-puesto-habitual" className="soem-label block mb-1">Puesto habitual</label>
                <input id="f-puesto-habitual" className={inputCls} value={form.position} onChange={e => set('position', e.target.value)} placeholder="Ej: Seguridad" />
              </div>
              <div>
                <label htmlFor="f-status" className="soem-label block mb-1">Status</label>
                <select id="f-status" className={inputCls} value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                </select>
              </div>
            </div>
          </div>
        </Section>

        {/* S2: Contact */}
        <Section title="Contacto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="f-teléfono" className="soem-label block mb-1">Teléfono</label>
              <input id="f-teléfono" 
                type="tel" 
                className={inputCls} 
                value={form.phone} 
                onChange={e => set('phone', formatPhoneInput(e.target.value))} 
                placeholder="10 dígitos" 
                maxLength={10}
              />
            </div>
            <div>
              <label htmlFor="f-correo-electrónico" className="soem-label block mb-1">Correo electrónico</label>
              <input id="f-correo-electrónico" type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
          </div>
          <div>
            <p className="soem-label mb-2">Domicilio</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label htmlFor="f-calle" className="text-xs text-slate-500 block mb-1">Calle</label>
                <input id="f-calle" className={inputCls} value={form.address.street} onChange={e => setNested('address', 'street', e.target.value)} />
              </div>
              <div>
                <label htmlFor="f-no-ext" className="text-xs text-slate-500 block mb-1">No. Ext</label>
                <input id="f-no-ext" className={inputCls} value={form.address.ext_number} onChange={e => setNested('address', 'ext_number', e.target.value)} />
              </div>
              <div>
                <label htmlFor="f-colonia" className="text-xs text-slate-500 block mb-1">Colonia</label>
                <input id="f-colonia" className={inputCls} value={form.address.colony} onChange={e => setNested('address', 'colony', e.target.value)} />
              </div>
              <div>
                <label htmlFor="f-cp" className="text-xs text-slate-500 block mb-1">CP</label>
                <input id="f-cp" className={inputCls} maxLength={5} value={form.address.postal_code} onChange={e => setNested('address', 'postal_code', e.target.value)} />
              </div>
              <div>
                <label htmlFor="f-ciudad" className="text-xs text-slate-500 block mb-1">Ciudad</label>
                <input id="f-ciudad" className={inputCls} value={form.address.city} onChange={e => setNested('address', 'city', e.target.value)} />
              </div>
            </div>
          </div>
        </Section>

        {/* S3: Bank info */}
        <Section title="💳 Datos bancarios" collapsible defaultOpen={false}>
          <p className="text-xs text-slate-400 bg-slate-50 p-2 rounded-sm border">Información para procesamiento de pagos</p>
          <div className="grid grid-cols-2 gap-3">
            {[['bank','Banco','BBVA / Santander...'], ['account_holder','Titular','Nombre completo'],
              ['account_number','Número de cuenta','10-18 dígitos'], ['clabe','CLABE interbancaria','18 dígitos']].map(([k, l, p]) => (
              <div key={k}>
                <label htmlFor={`f-bank-${k}`} className="soem-label block mb-1">{l}</label>
                <input id={`f-bank-${k}`} className={inputCls} value={form.bank_info[k] || ''} onChange={e => setNested('bank_info', k, e.target.value)} placeholder={p}
                  maxLength={k === 'clabe' ? 18 : undefined} />
              </div>
            ))}
          </div>
        </Section>

        {/* S4: Medical */}
        <Section title="🏥 Información médica" collapsible defaultOpen={false}>
          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-3 flex items-start gap-2 text-xs text-yellow-800">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            Información confidencial — solo visible para administradores
          </div>
          <div className="space-y-3">
            <div>
              <Toggle value={form.medical_info.has_condition} onChange={v => setNested('medical_info', 'has_condition', v)} label="¿Tiene alguna enfermedad o condición médica?" />
              {form.medical_info.has_condition && (
                <textarea className={`${inputCls} mt-2 resize-none`} rows={2} required value={form.medical_info.condition_detail}
                  onChange={e => setNested('medical_info', 'condition_detail', e.target.value)} placeholder="Describe la condición..." />
              )}
            </div>
            <div>
              <Toggle value={form.medical_info.uses_substances} onChange={v => setNested('medical_info', 'uses_substances', v)} label="¿Consume algún tipo de drogas o sustancias?" />
            </div>
            <div>
              <Toggle value={form.medical_info.has_disability} onChange={v => setNested('medical_info', 'has_disability', v)} label="¿Tiene alguna discapacidad?" />
              {form.medical_info.has_disability && (
                <textarea className={`${inputCls} mt-2 resize-none`} rows={2} required value={form.medical_info.disability_detail || ''}
                  onChange={e => setNested('medical_info', 'disability_detail', e.target.value)} placeholder="Describe la discapacidad..." />
              )}
            </div>
            <div>
              <label htmlFor="f-número-imss-opcional" className="soem-label block mb-1">Número IMSS (opcional)</label>
              <input id="f-número-imss-opcional" className={inputCls} value={form.imss_number} onChange={e => set('imss_number', e.target.value)} placeholder="12345678901234" />
            </div>
          </div>
        </Section>

        {/* S5: Emergency contact */}
        <Section title="Contacto de emergencia">
          <div className="grid grid-cols-2 gap-3">
            {[['name','Nombre completo *'], ['relationship','Parentesco'], ['phone','Teléfono *'], ['email','Email']].map(([k, l]) => (
              <div key={k}>
                <label htmlFor={`f-ec-${k}`} className="soem-label block mb-1">{l}</label>
                <input id={`f-ec-${k}`} className={inputCls} value={form.emergency_contact[k] || ''} onChange={e => setNested('emergency_contact', k, e.target.value)} />
              </div>
            ))}
          </div>
        </Section>

        <div className="flex gap-4">
          <button type="submit" disabled={saving || uploadingPhoto}
            className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-staff-btn">
            {saving
              ? <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Guardando datos...</>
              : uploadingPhoto
              ? <><span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Subiendo foto...</>
              : <><Save size={15} /> {isEdit ? 'Actualizar Personal' : 'Crear Personal'}</>
            }
          </button>
          <button type="button" onClick={() => navigate('/dashboard/staff')} className="soem-btn-secondary px-8 py-3 text-sm">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
